import { useEffect, useState, useRef } from "react";
import type { ExperimentKeyValue, ExperimentResult } from "@rflocal/shared/experiments";
import { getExperiment } from "../../../backendapi/experimentApi";
import { useExperimentContext } from "./ExperimentContext";

const DEFAULT_RESULT = (key: ExperimentKeyValue): ExperimentResult => ({
    key,
    enabled: false,
    variant: undefined
});

/** In-memory cache so re-renders / re-mounts don't re-fetch. */
const cache = new Map<string, ExperimentResult>();

/**
 * Evaluate an experiment via the backend.
 * Delegates the API call to `backendapi/experimentApi.ts` and caches the
 * result in memory for the lifetime of the page.
 *
 * The distinctId is provided by the ExperimentProvider at each app's root:
 * - Core app: reads currentWorkspaceId from globalStore (see RoboflowApp.tsx)
 * - Rapid: reads currentWorkspace.id from rapid's store (see App.tsx)
 *
 * This approach is adblocker-proof because the evaluation happens server-side
 * through posthog-node, not through the posthog-js client.
 *
 * @example
 * const { enabled, variant } = useExperiment(ExperimentKey.NewFeature);
 * if (enabled) { ... }
 */
export function useExperiment(key: ExperimentKeyValue): ExperimentResult {
    const { distinctId } = useExperimentContext();
    const cacheKey = `${key}:${distinctId}`;

    const [result, setResult] = useState<ExperimentResult>(
        () => cache.get(cacheKey) ?? DEFAULT_RESULT(key)
    );
    const fetchedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!distinctId) return;
        if (fetchedRef.current === cacheKey) return;
        fetchedRef.current = cacheKey;

        const cached = cache.get(cacheKey);
        if (cached) {
            setResult(cached);
            return;
        }

        const fetchExperiment = async () => {
            try {
                const data = await getExperiment(key, distinctId);
                cache.set(cacheKey, data);
                setResult(data);
            } catch {
                // Silently fall back to disabled on network errors
                setResult(DEFAULT_RESULT(key));
            }
        };

        fetchExperiment();
    }, [key, distinctId, cacheKey]);

    return result;
}
